import { toReduxError, type ReduxError, GENERIC_FETCH_ERROR_MESSAGE } from '@shared/errors';
import { produce } from 'immer';
import { ofType } from 'redux-observable';
import { Observable, OperatorFunction, of, from } from 'rxjs';
import { catchError, groupBy, map, mergeMap, switchMap, withLatestFrom } from 'rxjs/operators';
import { apiClient, ApiType, ApiPaths } from '@shared/apiClient';
import { Action, ActionType, createAsyncAction, createReducer } from 'typesafe-actions';
import { LocalDepartments, LocalDepartmentItem, LocationSlugs } from '@appTypes/nafLocations';
import { TypedEpic } from '../types';

export enum Actions {
  GET_NAF_LOCATIONS = 'naf/centers/GET_NAF_LOCATIONS',
  GET_NAF_LOCATIONS_SUCCESS = 'naf/centers/GET_NAF_LOCATIONS_SUCCESS',
  GET_NAF_LOCATIONS_FAIL = 'naf/centers/GET_NAF_LOCATIONS_FAIL',
  GET_NAF_LOCATIONS_CANCEL = 'naf/centers/GET_NAF_LOCATIONS_CANCEL',
}

/** Whole list in one request — a few hundred items, filtered client-side. Shared with the SSR prefetch. */
export const NAF_LOCATIONS_TAKE = 1000;

type FetchState = 'initial' | 'isUpdating' | 'success' | 'error' | 'cancelled';

export interface State {
  locations: Record<
    LocationSlugs,
    {
      data?: LocalDepartments[] | LocalDepartmentItem[];
      totalCount?: number | null;
      fetchState: FetchState;
      error?: ReduxError;
      previousTake?: number;
      previousCounties?: string[];
    }
  >;
}

export const initialState: State = {
  locations: {
    [LocationSlugs.lokalavdeling]: {
      fetchState: 'initial',
    },
    [LocationSlugs.mc]: {
      fetchState: 'initial',
    },
    [LocationSlugs.nafCenters]: {
      fetchState: 'initial',
    },
    [LocationSlugs.practiceTracks]: {
      fetchState: 'initial',
    },
  },
};

export const actions = {
  getNafLocations: createAsyncAction(
    Actions.GET_NAF_LOCATIONS, // request payload creator
    Actions.GET_NAF_LOCATIONS_SUCCESS, // success payload creator
    Actions.GET_NAF_LOCATIONS_FAIL, // failure payload creator
  )<
    { search: LocationSlugs; take?: number; counties?: string[]; ignoreCounties?: boolean },
    {
      items: LocalDepartments[] | LocalDepartmentItem[];
      total: number;
      name: LocationSlugs;
      take: number;
      counties?: string[];
    },
    { error: ReduxError; name: LocationSlugs }
  >(),
};

export const reducers = createReducer<State, Action>(initialState, {})
  .handleAction(actions.getNafLocations.request, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.locations[action.payload.search] = {
        ...draftState.locations[action.payload.search],
        fetchState: 'isUpdating',
      };
    }),
  )
  .handleAction(actions.getNafLocations.success, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.locations[action.payload.name] = {
        data: action.payload.items,
        totalCount: action.payload.total,
        fetchState: 'success',
        error: undefined,
        previousTake: action.payload.take,
        previousCounties: action.payload.counties,
      };
    }),
  )
  .handleAction(actions.getNafLocations.failure, (state = initialState, action) =>
    produce(state, (draftState) => {
      draftState.locations[action.payload.name] = {
        ...draftState.locations[action.payload.name],
        fetchState: 'error',
        error: action.payload.error,
      };
    }),
  );

const fetchNafLocationsEpic: TypedEpic = (action$: Observable<Action<any>>, state$) =>
  action$.pipe(
    ofType(Actions.GET_NAF_LOCATIONS),
    groupBy((action) => (action as ActionType<typeof actions.getNafLocations.request>).payload.search),
    mergeMap((group$) =>
      group$.pipe(
        withLatestFrom(state$) as unknown as OperatorFunction<
          Action<any>,
          ActionType<typeof actions.getNafLocations.request>[]
        >,
        switchMap(([action]) => {
          const { search, ...params } = action.payload;
          // Filter out empty/null params and only include non-empty values
          const filteredParams = Object.fromEntries(Object.entries(params).filter(([, value]) => Boolean(value)));

          return from(
            apiClient.get<{ items: LocalDepartments[]; total: number }>(ApiType.apimNafNoApi, {
              url: ApiPaths[ApiType.apimNafNoApi].nafLocations.search(search),
              params: filteredParams,
            }),
          ).pipe(
            map((response) => {
              if (Array.isArray(response?.data?.items)) {
                return actions.getNafLocations.success({
                  ...response.data,
                  name: action.payload.search,
                  take: action.payload.take,
                  counties: action.payload.counties,
                });
              }
              return actions.getNafLocations.failure({
                error: toReduxError(undefined, GENERIC_FETCH_ERROR_MESSAGE),
                name: action.payload.search,
              });
            }),
            catchError((err) =>
              of(
                actions.getNafLocations.failure({
                  error: toReduxError(err, GENERIC_FETCH_ERROR_MESSAGE),
                  name: action.payload.search,
                }),
              ),
            ),
          );
        }),
      ),
    ),
  );

export const epics: TypedEpic[] = [fetchNafLocationsEpic];
